/**
 * Núcleo do i18n: idiomas, chaves tipadas, tradução e formatação.
 *
 * Duas regras moldam este arquivo:
 *
 * 1. **Chave errada é erro de compilação.** O tipo `Chave` nasce do dicionário
 *    `pt-BR`, então `t("elenco.naoExiste")` nem chega a rodar. Texto faltando na
 *    tela é o tipo de defeito que só aparece com o jogo na mão do jogador, e o
 *    compilador é quem pode pegar isso antes.
 * 2. **Formatação mora aqui.** Número, dinheiro e data saem de um lugar só, com
 *    `Intl`, em vez de cada tela inventar o seu. Trocar de idioma é trocar o
 *    dicionário e a etiqueta de local, sem caçar `toLocaleString` pelas telas.
 *
 * Um segundo idioma entra somando o arquivo dele em `DICIONARIOS` e a etiqueta
 * em `LOCAIS`. O tipo `Idioma` cresce sozinho, e o compilador cobra tanto a
 * etiqueta que faltar quanto qualquer chave que o dicionário novo não tiver.
 */
import { en } from "./en";
import { anoDaTemporada } from "@legacy-manager/engine";
import { es } from "./es";
import { ptBR } from "./pt-BR";

// ---------------------------------------------------------------------------
// Tipos derivados do dicionário
// ---------------------------------------------------------------------------

/** Qualquer galho da árvore de textos: ou o texto, ou outro galho. */
type Galho = { readonly [chave: string]: string | Galho };

/** A mesma forma de um dicionário, com os textos soltos do valor literal. */
type MesmaForma<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : MesmaForma<T[K]>;
};

/** Todos os caminhos de texto de um dicionário, em notação de ponto. */
type Caminhos<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${Caminhos<T[K]>}`;
}[keyof T & string];

/** Forma que todo dicionário precisa ter, cobrada de cada idioma novo. */
export type Dicionario = MesmaForma<typeof ptBR>;

/** Toda chave válida de texto, como `"hub.assistir"`. */
export type Chave = Caminhos<typeof ptBR>;

/** Valores que entram nos marcadores `{assim}` do texto. */
export type Valores = Readonly<Record<string, string | number>>;

/** Assinatura da função de tradução, a mesma do gancho e da função solta. */
export type Traduzir = (chave: Chave, valores?: Valores) => string;

// ---------------------------------------------------------------------------
// Idiomas
// ---------------------------------------------------------------------------

/**
 * Os dicionários que o jogo carrega.
 *
 * Nenhuma tela mudou para o inglês e o espanhol entrarem: cada um é uma linha
 * aqui e uma etiqueta em `LOCAIS`. O `satisfies` é o que cobra a forma de cada
 * dicionário novo contra a do português.
 */
const DICIONARIOS = {
  "pt-BR": ptBR,
  en,
  es,
} satisfies Record<string, Dicionario>;

export type Idioma = keyof typeof DICIONARIOS;

/**
 * Etiqueta de local do `Intl` para cada idioma.
 *
 * `en` e `es` vão sem região de propósito. O jogo não é de nenhum país: a
 * moeda é o `$` genérico e a região só mudaria separador e ordem de data por um
 * palpite sobre onde a pessoa mora. Sem região, o `Intl` usa a forma mais
 * comum do idioma, que é o que o jogador espera ler.
 */
const LOCAIS: Record<Idioma, string> = {
  "pt-BR": "pt-BR",
  en: "en",
  es: "es",
};

/**
 * Nome de cada idioma, sempre escrito no próprio idioma.
 *
 * Não mora nos dicionários porque não pode mudar com o idioma ativo: quem
 * precisa do seletor é justamente quem não lê a língua que está na tela, e uma
 * lista que diga "Espanhol" para quem só lê espanhol não serve para nada.
 */
export const NOMES_DOS_IDIOMAS: Record<Idioma, string> = {
  "pt-BR": "Português",
  en: "English",
  es: "Español",
};

export const IDIOMA_PADRAO: Idioma = "pt-BR";

/** Lista para uma futura tela de opções montar o seletor de idioma. */
export const IDIOMAS: Idioma[] = Object.keys(DICIONARIOS) as Idioma[];

/**
 * Idioma que a função solta `t` usa.
 *
 * O provedor mantém este valor igual ao do estado do React. Existe porque nem
 * todo texto nasce dentro de um componente: há função auxiliar de tela e há
 * formatação chamada de fora do React, e ninguém precisa carregar o idioma na
 * mão até lá.
 */
let ativo: Idioma = IDIOMA_PADRAO;

export function idiomaAtivo(): Idioma {
  return ativo;
}

export function definirIdiomaAtivo(idioma: Idioma): void {
  ativo = idioma;
}

// ---------------------------------------------------------------------------
// Tradução
// ---------------------------------------------------------------------------

const MARCADOR = /\{(\w+)\}/g;

/** Desce a árvore do dicionário pelo caminho separado por ponto. */
function buscar(dicionario: Galho, chave: string): string | null {
  let atual: string | Galho | undefined = dicionario;
  for (const parte of chave.split(".")) {
    if (typeof atual !== "object") return null;
    atual = atual[parte];
  }
  return typeof atual === "string" ? atual : null;
}

/** Troca cada `{marcador}` pelo valor recebido. O que não vier fica como está. */
function interpolar(texto: string, valores: Valores | undefined): string {
  if (!valores) return texto;
  return texto.replace(MARCADOR, (inteiro, nome: string) => {
    const valor = valores[nome];
    return valor === undefined ? inteiro : String(valor);
  });
}

/**
 * Texto de uma chave, no idioma pedido.
 *
 * Sem o idioma, usa o idioma ativo. Se um dicionário novo esquecer uma chave, o
 * texto em português aparece no lugar: tela com palavra fora de idioma incomoda,
 * tela com buraco não dá para jogar.
 */
export function traduzir(chave: Chave, valores?: Valores, idioma: Idioma = ativo): string {
  const texto = buscar(DICIONARIOS[idioma] as Galho, chave) ?? buscar(ptBR as Galho, chave);
  return interpolar(texto ?? chave, valores);
}

/** Atalho de `traduzir` no idioma ativo, para quem não é componente React. */
export const t: Traduzir = (chave, valores) => traduzir(chave, valores);

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------

const formatosDeNumero = new Map<string, Intl.NumberFormat>();

function formatoDeNumero(idioma: Idioma, opcoes?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const local = LOCAIS[idioma];
  const assinatura = `${local}|${opcoes?.maximumFractionDigits ?? ""}`;
  const guardado = formatosDeNumero.get(assinatura);
  if (guardado) return guardado;
  const novo = new Intl.NumberFormat(local, opcoes);
  formatosDeNumero.set(assinatura, novo);
  return novo;
}

/** Número com separador de milhar do idioma. */
export function numero(valor: number, idioma: Idioma = ativo): string {
  return formatoDeNumero(idioma).format(valor);
}

/**
 * Dinheiro do jogo em formato compacto.
 *
 * A moeda é o `$` genérico do FRONTEND_GUIDE, e não a de nenhum país: o mundo do
 * jogo mistura ligas de continentes diferentes e converter valor entre elas só
 * atrapalharia a comparação que o jogador faz o tempo todo.
 */
export function dinheiro(valor: number, idioma: Idioma = ativo): string {
  const absoluto = Math.abs(valor);
  const sinal = valor < 0 ? traduzir("comum.dinheiro.negativo", undefined, idioma) : "";
  const formato = formatoDeNumero(idioma, { maximumFractionDigits: 1 });
  if (absoluto >= 1_000_000_000) {
    return traduzir(
      "comum.dinheiro.bilhoes",
      { sinal, valor: formato.format(absoluto / 1_000_000_000) },
      idioma,
    );
  }
  if (absoluto >= 1_000_000) {
    return traduzir(
      "comum.dinheiro.milhoes",
      { sinal, valor: formato.format(absoluto / 1_000_000) },
      idioma,
    );
  }
  if (absoluto >= 1_000) {
    return traduzir(
      "comum.dinheiro.milhares",
      { sinal, valor: formato.format(absoluto / 1_000) },
      idioma,
    );
  }
  return traduzir("comum.dinheiro.unidade", { sinal, valor: formato.format(absoluto) }, idioma);
}

const DIAS_DA_SEMANA = [
  "domingo",
  "segunda",
  "terca",
  "quarta",
  "quinta",
  "sexta",
  "sabado",
] as const;

const MESES = [
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez",
] as const;

/**
 * Data de exibição derivada da rodada.
 *
 * O motor conta rodadas, não dias, então o calendário é montado aqui: a
 * temporada abre em 15 de janeiro e cada rodada anda cinco dias. Os nomes de dia
 * e de mês vêm do dicionário em vez do `Intl.DateTimeFormat` porque a tela usa a
 * forma curta com inicial maiúscula, que o `Intl` não oferece em pt-BR.
 */
export function dataDaRodada(temporada: number, rodada: number, idioma: Idioma = ativo): string {
  /*
   * O ano vem do motor, e não de um 2025 escrito aqui.
   *
   * O clube criado nasce no ano da temporada corrente, e quem faz essa conta é
   * o motor. Com o número em dois lugares, um clube fundado em 2026 podia
   * aparecer numa tela que chamava o mesmo momento de 2025.
   */
  const base = new Date(Date.UTC(anoDaTemporada(temporada), 0, 15));
  base.setUTCDate(base.getUTCDate() + rodada * 5);
  const chaveDoMes = MESES[base.getUTCMonth()];
  const chaveDoDia = DIAS_DA_SEMANA[base.getUTCDay()];
  return traduzir(
    "comum.data.rodada",
    {
      dia: base.getUTCDate(),
      mes: chaveDoMes ? traduzir(`comum.data.meses.${chaveDoMes}`, undefined, idioma) : "",
      ano: base.getUTCFullYear(),
      diaDaSemana: chaveDoDia
        ? traduzir(`comum.data.diasDaSemana.${chaveDoDia}`, undefined, idioma)
        : "",
    },
    idioma,
  );
}

const formatosDeData = new Map<string, Intl.DateTimeFormat>();

function formatoDeData(idioma: Idioma, opcoes: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const local = LOCAIS[idioma];
  const assinatura = `${local}|${opcoes.dateStyle ?? ""}|${opcoes.timeStyle ?? ""}`;
  const guardado = formatosDeData.get(assinatura);
  if (guardado) return guardado;
  const novo = new Intl.DateTimeFormat(local, opcoes);
  formatosDeData.set(assinatura, novo);
  return novo;
}

/**
 * Data e hora de um instante do servidor, que chega em ISO 8601.
 *
 * O modo online fala em dia de verdade e em prazo de relógio, não em rodada,
 * então `dataDaRodada` não serve para ele. Texto fora do formato volta como
 * está: prazo estranho na tela é melhor que tela quebrada.
 */
export function dataEHora(iso: string, idioma: Idioma = ativo): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return iso;
  return formatoDeData(idioma, { dateStyle: "short", timeStyle: "short" }).format(quando);
}

/** Só o dia, para renovação de assinatura e prazo de exclusão de conta. */
export function dataLonga(iso: string, idioma: Idioma = ativo): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return iso;
  return formatoDeData(idioma, { dateStyle: "long" }).format(quando);
}

/**
 * Contagem regressiva do prazo da rodada.
 *
 * A unidade encolhe conforme o prazo chega: dias e horas quando falta muito,
 * minutos e segundos no fim. Um relógio que mostra "2d 4h 13min 07s" o dia
 * inteiro só pisca sem dizer nada.
 */
export function contagemRegressiva(ms: number, idioma: Idioma = ativo): string {
  if (ms <= 0) return traduzir("comum.tempo.acabou", undefined, idioma);
  const segundos = Math.floor(ms / 1000);
  const dias = Math.floor(segundos / 86_400);
  const horas = Math.floor((segundos % 86_400) / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  if (dias > 0) return traduzir("comum.tempo.contagemDias", { dias, horas }, idioma);
  if (horas > 0) return traduzir("comum.tempo.contagemHoras", { horas, minutos }, idioma);
  return traduzir(
    "comum.tempo.contagemMinutos",
    { minutos, segundos: String(segundos % 60).padStart(2, "0") },
    idioma,
  );
}

/** Quanto tempo faz, em texto curto, para a lista de carreiras salvas. */
export function tempoRelativo(quando: number, idioma: Idioma = ativo): string {
  const minutos = Math.floor((Date.now() - quando) / 60_000);
  if (minutos < 1) return traduzir("comum.tempo.agora", undefined, idioma);
  if (minutos < 60) return traduzir("comum.tempo.minutos", { valor: minutos }, idioma);
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return traduzir("comum.tempo.horas", { valor: horas }, idioma);
  return traduzir("comum.tempo.dias", { valor: Math.floor(horas / 24) }, idioma);
}

// ---------------------------------------------------------------------------
// Rótulos vindos do motor
// ---------------------------------------------------------------------------

/**
 * Nome da característica pela sigla que o motor usa.
 *
 * A sigla desconhecida volta como está, que é como a tela se comportava antes:
 * atributo novo no motor aparece cru em vez de sumir da ficha.
 */
export function rotuloDeCaracteristica(sigla: string, idioma: Idioma = ativo): string {
  const texto = buscar(DICIONARIOS[idioma] as Galho, `comum.caracteristicas.${sigla}`);
  return texto ?? sigla;
}

/**
 * Pé bom e personalidade, pela sigla do motor.
 *
 * As duas tabelas moram no bloco do editor de bancos, que foi quem primeiro
 * precisou delas escritas. Ficam lá, e não copiadas num segundo lugar, porque
 * são a mesma palavra: duas cópias divergem sozinhas na primeira revisão de
 * texto. Estas funções existem para que as telas não precisem saber disso.
 */
export function rotuloDePe(pe: string, idioma: Idioma = ativo): string {
  const texto = buscar(DICIONARIOS[idioma] as Galho, `editor.pes.${pe}`);
  return texto ?? pe;
}

export function rotuloDePersonalidade(personalidade: string, idioma: Idioma = ativo): string {
  const texto = buscar(DICIONARIOS[idioma] as Galho, `editor.personalidades.${personalidade}`);
  return texto ?? personalidade;
}

/** Nome do setor do campo pela sigla do motor. */
export function rotuloDeSetor(
  setor: "GOL" | "DEF" | "MEI" | "ATA",
  idioma: Idioma = ativo,
): string {
  return traduzir(`comum.setores.${setor}`, undefined, idioma);
}

/*
 * As duas tabelas abaixo são siglas do motor, e não texto de tela, por isso
 * ficam aqui e não nos dicionários.
 *
 * Em português elas já saem prontas: `ZAG` é como o brasileiro escreve zagueiro
 * e `V` é como ele escreve vitória, então o dicionário só repetiria a sigla como
 * tradução dela mesma. Nos outros idiomas existe a sigla consagrada, e é ela que
 * precisa aparecer: quem lê inglês não reconhece `ZAG`, reconhece `CB`.
 *
 * Sigla que o motor invente e não esteja na tabela volta como veio, que é o
 * mesmo caminho de `rotuloDeCaracteristica`: posição nova aparece crua em vez de
 * sumir da escalação.
 */

const POSICOES: Record<Idioma, Readonly<Record<string, string>>> = {
  "pt-BR": {},
  en: {
    GOL: "GK",
    ZAG: "CB",
    LD: "RB",
    LE: "LB",
    VOL: "DM",
    MC: "CM",
    MEI: "AM",
    PD: "RW",
    PE: "LW",
    ATA: "ST",
  },
  es: {
    GOL: "POR",
    ZAG: "DFC",
    LD: "LD",
    LE: "LI",
    VOL: "MCD",
    MC: "MC",
    MEI: "MCO",
    PD: "ED",
    PE: "EI",
    ATA: "DC",
  },
};

/** Sigla da posição do jogador, na forma que cada idioma usa. */
export function rotuloDePosicao(posicao: string, idioma: Idioma = ativo): string {
  return POSICOES[idioma][posicao] ?? posicao;
}

const RESULTADOS: Record<Idioma, Readonly<Record<string, string>>> = {
  "pt-BR": {},
  en: { V: "W", E: "D", D: "L" },
  es: { V: "G", E: "E", D: "P" },
};

/** Letra de vitória, empate ou derrota, para a faixa de últimos jogos. */
export function rotuloDeResultado(resultado: string, idioma: Idioma = ativo): string {
  return RESULTADOS[idioma][resultado] ?? resultado;
}

// ---------------------------------------------------------------------------
// Texto que o motor manda como chave
// ---------------------------------------------------------------------------

/**
 * O que o motor guarda no save no lugar da frase.
 *
 * A notícia deixou de ser texto e passou a ser `{ key, params }`. Quem escreve
 * a frase é o dicionário, aqui, na hora de mostrar. É por isso que trocar a
 * redação de uma notícia hoje conserta também as que já estão guardadas numa
 * carreira de cinco temporadas atrás.
 *
 * As chaves não passam pelo tipo `Chave` de propósito: elas nascem no motor, o
 * cliente as recebe de dentro de um save que pode ser mais velho que esta
 * versão do jogo, e chave desconhecida precisa ter um caminho de volta em vez
 * de virar erro. Quem cobra a lista inteira é `motor.test.ts`, que compara o
 * catálogo do motor com os três dicionários.
 */
export type ValoresDoMotor = Readonly<Record<string, string | number>>;

/** Valor de dinheiro que ainda não foi escrito: `$` colado num número inteiro. */
const DINHEIRO_CRU = /^\$-?\d+$/;

/**
 * Resolve um valor de parâmetro antes de ele entrar na frase.
 *
 * Três casos saem do literal. Valor que começa por `@` é outra chave do
 * dicionário, e é assim que o rótulo do clássico entra no meio da notícia sem o
 * motor precisar escrevê-lo em três línguas. Valor que é `$` colado num número
 * é dinheiro ainda não escrito, e vira o formato de quem está lendo: o
 * `formatMoney` do motor abrevia em português ("$ 753 mil"), e isso no meio de
 * uma frase em inglês é uma palavra fora do idioma. O parâmetro `posicao`
 * carrega a sigla do motor, que cada idioma escreve do seu jeito: quem lê
 * inglês não reconhece `ZAG`, reconhece `CB`.
 *
 * O pedaço que entra por `@` é interpolado com os mesmos valores da frase que o
 * recebe. É o que permite ao motivo de uma recusa dizer **qual** jogador está
 * lesionado: a notícia manda `{ motivo: "@escalacao.lesionado", jogador: "Ana" }`
 * e o `{jogador}` de dentro do motivo encontra o valor aqui. Sem isso a frase
 * sairia inteira e sem o nome, que é o tipo de frase que mente.
 */
function valorDoMotor(
  nome: string,
  valor: string | number,
  valores: ValoresDoMotor,
  idioma: Idioma,
): string | number {
  if (typeof valor !== "string") return valor;
  if (valor.startsWith("@")) return pedacoDoMotor(valor.slice(1), valores, idioma);
  if (DINHEIRO_CRU.test(valor)) return dinheiro(Number(valor.slice(1)), idioma);
  if (nome === "posicao") return rotuloDePosicao(valor, idioma);
  return valor;
}

function valoresResolvidos(valores: ValoresDoMotor, idioma: Idioma): Valores {
  const prontos: Record<string, string | number> = {};
  for (const [nome, valor] of Object.entries(valores)) {
    prontos[nome] = valorDoMotor(nome, valor, valores, idioma);
  }
  return prontos;
}

/** O texto de um caminho, no idioma pedido, com o português como rede. */
function acharTexto(caminho: string, idioma: Idioma): string | null {
  return buscar(DICIONARIOS[idioma] as Galho, caminho) ?? buscar(ptBR as Galho, caminho);
}

/**
 * Meia frase que chega como `@chave`: um rótulo, um motivo ou um pedaço da tela.
 *
 * Procura nos três lugares porque os três entram do mesmo jeito na frase de
 * fora. A diferença é que o rótulo nunca tem marcador e o motivo pode ter, e é
 * por isso que os valores da frase de fora chegam até aqui. O terceiro lugar é
 * o caminho inteiro do dicionário, que é como a sessão solo encaixa um pedaço
 * seu (`@sessao.fila.jogadorDesconhecido`) dentro de outra frase dela.
 */
function pedacoDoMotor(chave: string, valores: ValoresDoMotor, idioma: Idioma): string {
  const texto =
    acharTexto(`motor.rotulos.${chave}`, idioma) ??
    acharTexto(`motor.motivos.${chave}`, idioma) ??
    acharTexto(chave, idioma);
  if (texto === null || texto === undefined) return chave;
  const prontos: Record<string, string | number> = {};
  for (const [nome, valor] of Object.entries(valores)) {
    if (typeof valor === "string" && valor.startsWith("@")) continue;
    prontos[nome] = nome === "posicao" ? rotuloDePosicao(valor as string, idioma) : valor;
  }
  return interpolar(texto, prontos);
}

/**
 * Título e corpo de uma notícia do motor.
 *
 * Devolve `null` quando a chave não existe no dicionário, e quem chama mostra a
 * notícia como ela veio. Isso acontece em duas situações reais: save gravado
 * por uma versão mais nova do motor, e notícia antiga que nunca teve chave.
 * Nas duas, texto fora do idioma é melhor que notícia em branco.
 */
export function noticiaDoMotor(
  chave: string,
  valores: ValoresDoMotor = {},
  idioma: Idioma = ativo,
): { titulo: string; corpo: string } | null {
  const titulo =
    buscar(DICIONARIOS[idioma] as Galho, `motor.noticias.${chave}.titulo`) ??
    buscar(ptBR as Galho, `motor.noticias.${chave}.titulo`);
  const corpo =
    buscar(DICIONARIOS[idioma] as Galho, `motor.noticias.${chave}.corpo`) ??
    buscar(ptBR as Galho, `motor.noticias.${chave}.corpo`);
  if (titulo === null || corpo === null) return null;
  const prontos = valoresResolvidos(valores, idioma);
  return { titulo: interpolar(titulo, prontos), corpo: interpolar(corpo, prontos) };
}

/** Uma linha do "Por que aconteceu" da partida. */
export function fatorDaPartida(
  chave: string,
  valores: ValoresDoMotor = {},
  idioma: Idioma = ativo,
): string {
  // A chave do motor vem como `fator.posse`; no dicionário ela mora em
  // `motor.fatores.posse`, agrupada com as outras.
  const nome = chave.startsWith("fator.") ? chave.slice("fator.".length) : chave;
  const texto =
    buscar(DICIONARIOS[idioma] as Galho, `motor.fatores.${nome}`) ??
    buscar(ptBR as Galho, `motor.fatores.${nome}`);
  if (texto === null) return chave;
  return interpolar(texto, valoresResolvidos(valores, idioma));
}

/**
 * Um lance da partida, escrito.
 *
 * É o texto que mais aparece na frente de quem joga: passa lance a lance no
 * modo ao vivo e volta inteiro no relatório. Chave desconhecida volta como
 * veio, porque lance em branco no meio da narração é pior que chave crua.
 */
export function lanceDaPartida(
  chave: string,
  valores: ValoresDoMotor = {},
  idioma: Idioma = ativo,
): string {
  // A chave do motor vem como `lance.golPenalti`; no dicionário ela mora em
  // `motor.lances.golPenalti`, agrupada com as outras.
  const nome = chave.startsWith("lance.") ? chave.slice("lance.".length) : chave;
  const texto =
    buscar(DICIONARIOS[idioma] as Galho, `motor.lances.${nome}`) ??
    buscar(ptBR as Galho, `motor.lances.${nome}`);
  if (texto === null) return chave;
  return interpolar(texto, valoresResolvidos(valores, idioma));
}

/**
 * Por que o motor recusou uma ordem.
 *
 * O mesmo motivo que vira `{motivo}` dentro de uma notícia também chega aqui
 * cru, quando a tela pergunta na hora: escalação recusada, empréstimo negado,
 * olheiro parado, banco que não libera.
 */
export function motivoDoMotor(
  chave: string,
  valores: ValoresDoMotor = {},
  idioma: Idioma = ativo,
): string {
  return pedacoDoMotor(chave, valores, idioma);
}

/**
 * Uma observação do laudo de olheiro.
 *
 * `key: null` é laudo de save antigo, que guardava a frase pronta: ela volta
 * como está, fora do idioma, que é melhor que linha em branco.
 */
export function notaDeLaudo(
  nota: { key: string } | { key: null; text: string },
  idioma: Idioma = ativo,
): string {
  if (nota.key === null) return nota.text;
  const nome = nota.key.startsWith("laudo.") ? nota.key.slice("laudo.".length) : nota.key;
  const texto =
    buscar(DICIONARIOS[idioma] as Galho, `motor.laudo.${nome}`) ??
    buscar(ptBR as Galho, `motor.laudo.${nome}`);
  return texto ?? nota.key;
}

/** Rótulo solto do motor: rivalidade, veredito do olheiro, porte do clube. */
export function rotuloDoMotor(chave: string, idioma: Idioma = ativo): string {
  const texto =
    buscar(DICIONARIOS[idioma] as Galho, `motor.rotulos.${chave}`) ??
    buscar(ptBR as Galho, `motor.rotulos.${chave}`);
  return texto ?? chave;
}

/** Porte do clube pela reputação de 1 a 6, como a tela de escolha mostra. */
export function rotuloDeReputacao(reputacao: number, idioma: Idioma = ativo): string {
  const nivel = Math.min(6, Math.max(1, Math.round(reputacao)));
  return rotuloDoMotor(`reputacao.${nivel}`, idioma);
}

/**
 * A fase de um jogo, escrita.
 *
 * O motor guarda em `stage` um identificador, e são quatro formas dele. Pontos
 * corridos ("liga", "estadual") não têm fase: o nome da competição já diz tudo,
 * e a tela mostra a rodada. A fase de grupos é `Grupo A`, com a letra dentro do
 * identificador. O mata-mata é uma chave do catálogo (`quartas`). E a fase de
 * tamanho incomum, que só aparece em banco com mais de 128 clubes num país, é
 * `fase256`, escrita pelo número: dar nome próprio a cada tamanho seria inventar
 * nome que ninguém usa.
 */
export function rotuloDeFase(fase: string, idioma: Idioma = ativo): string {
  if (fase === "" || fase === "liga" || fase === "estadual") return "";
  const grupo = /^Grupo (.+)$/.exec(fase);
  if (grupo) return traduzir("motor.rotulos.fase.grupo", { letra: grupo[1] ?? "" }, idioma);
  const tamanho = /^fase(\d+)$/.exec(fase);
  if (tamanho) {
    return traduzir("motor.rotulos.fase.deClubes", { clubes: Number(tamanho[1]) }, idioma);
  }
  return rotuloDoMotor(`fase.${fase}`, idioma);
}

/**
 * Nome e descrição de uma conquista de carreira.
 *
 * O motor manda a chave, nunca a frase: o identificador da conquista
 * (`primeiro-titulo`) fica gravado na carreira e não muda, e o nome que a tela
 * mostra passou a ser deste dicionário. É o que faz uma conquista de dez
 * temporadas atrás aparecer no idioma de quem está lendo hoje.
 */
export function conquistaDoMotor(
  chave: string,
  idioma: Idioma = ativo,
): { nome: string; descricao: string } {
  const nome = chave.startsWith("conquista.") ? chave.slice("conquista.".length) : chave;
  const parte = (qual: string): string =>
    buscar(DICIONARIOS[idioma] as Galho, `motor.conquistas.${nome}.${qual}`) ??
    buscar(ptBR as Galho, `motor.conquistas.${nome}.${qual}`) ??
    chave;
  return { nome: parte("nome"), descricao: parte("descricao") };
}

/**
 * O que há de errado num banco de dados, dito a quem o editou.
 *
 * O caminho do problema não passa por aqui: ele é endereço, não texto, e sai
 * igual em qualquer idioma.
 */
export function problemaDeDatapack(
  chave: string,
  valores: ValoresDoMotor = {},
  idioma: Idioma = ativo,
): string {
  const nome = chave.startsWith("datapack.") ? chave.slice("datapack.".length) : chave;
  const texto =
    buscar(DICIONARIOS[idioma] as Galho, `motor.datapack.${nome}`) ??
    buscar(ptBR as Galho, `motor.datapack.${nome}`);
  if (texto === null) return chave;
  return interpolar(texto, valoresResolvidos(valores, idioma));
}

/**
 * A frase de algo que nasceu como chave do outro lado do Web Worker.
 *
 * `src/game/` roda dentro do worker, que tem a própria cópia deste módulo e
 * **nunca soube qual idioma a pessoa escolheu**: qualquer frase escrita lá sai
 * em português para todo mundo. Por isso a camada de jogo devolve chave e
 * valores, e é aqui, na thread da tela, que a frase é escrita.
 *
 * Três origens caem neste caminho, e por isso a busca tem dois lugares. A
 * sessão solo recusa pela própria lista (`sessao.janelaFechada`), que mora no
 * dicionário da tela porque é regra do cliente e não do motor. O motor recusa a
 * abertura de um save por chave de save (`save.deVersaoMaisNova`), que mora
 * junto do resto do texto dele. E o motor recusa ordens por chave de motivo
 * (`emprego.foraDaJanela`), que já tinha caminho próprio e continua tendo.
 */
export function textoDaSessao(
  chave: string,
  valores: ValoresDoMotor = {},
  idioma: Idioma = ativo,
): string {
  // `sessao.*` é caminho de dicionário da tela; o resto é texto do motor, que
  // mora sob `motor.`. Chave que não for nenhum dos dois cai no motivo.
  const caminho = chave.startsWith("sessao.") ? chave : `motor.${chave}`;
  const texto = acharTexto(caminho, idioma);
  if (texto === null) return pedacoDoMotor(chave, valores, idioma);
  return interpolar(texto, valoresResolvidos(valores, idioma));
}

/**
 * Mensagem de um erro da API, pelo código estável do envelope (docs/API.md 1).
 *
 * Devolve `null` quando o código é desconhecido, e aí quem chama mostra a
 * mensagem que o servidor mandou. O servidor responde sempre em português, e
 * uma tela em inglês com aviso em português parece defeito; mas código novo no
 * servidor com cliente velho é caso real, e nele a mensagem do servidor é a
 * única coisa que explica o que houve.
 */
export function mensagemDoCodigo(codigo: string, idioma: Idioma = ativo): string | null {
  return (
    buscar(DICIONARIOS[idioma] as Galho, `errosDaApi.${codigo}`) ??
    buscar(ptBR as Galho, `errosDaApi.${codigo}`)
  );
}
